import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonDirective,
  FormControlDirective,
  FormFeedbackComponent,
  InputGroupComponent,
  InputGroupTextDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule, IconSetService } from '@coreui/icons-angular';
import { cilUser, cilEnvelopeClosed } from '@coreui/icons';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';
import { Logger, UnassistedInputDirective } from 'shared';

import { FunnelService } from '../../../services/funnel.service';
import {
  FunnelIntent,
  SESSION_SPONSOR_KEY,
  normaliseIntent,
  readSiteReferral,
  rememberReferral,
  resolveSponsor,
  readStoredIntent,
  storeIntent,
  validSponsorId
} from '../../../services/funnel-intent';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'WolfPack Membership Funnel';

/** The five marketing screens, then the signup form. */
const LAST_CONTENT_SCREEN = 5;
type Screen = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * WolfPack "ONE membership" funnel (Global Health, config_domain_id 5).
 *
 * Five marketing screens — hook, benefits, referral depth, "keep it simple",
 * the decision — and then the signup form. There is deliberately no quiz and no
 * plan comparison: the visitor is never asked "which membership is right for
 * me?", because there is only one. The two prices ($9.99/week, $399/year) are
 * a payment rhythm, not a choice of product, and the rhythm is picked later in
 * the purchase wizard.
 *
 * The signup half is the same lead capture the retired Health Finder landing
 * used (name + e-mail + mCaptcha -> magic-link mail), so the account creation,
 * sponsor attribution and captcha behaviour are unchanged — only the screens in
 * front of it are new. The retired funnel itself still lives in
 * funnel-landing-page / funnel-quiz-* and comes back by flipping
 * environment.globalHealthFunnelMode (and the backend's GLOBALHEALTH_FUNNEL_MODE).
 */
@Component({
  selector: 'app-funnel-join-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
    FormControlDirective,
    FormFeedbackComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    IconDirective,
    IconModule,
    UnassistedInputDirective,
    TranslatePipe,
    FunnelShellComponent
  ],
  templateUrl: './funnel-join-page.component.html',
  styleUrls: ['./funnel-join-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelJoinPageComponent {
  private fb         = inject(FormBuilder);
  private http       = inject(HttpClient);
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);
  private iconSet    = inject(IconSetService);
  private logger     = new Logger('FunnelJoinPage');

  readonly lastContentScreen = LAST_CONTENT_SCREEN;
  screen = signal<Screen>(1);
  /** The signup form only exists on the final screen. */
  onSignup = computed(() => this.screen() > LAST_CONTENT_SCREEN);

  form!: FormGroup;

  captchaToken   = signal<string | null>(null);
  captchaError   = signal<boolean>(false);
  mcaptchaConfig = signal<{ widgetUrl: string; sitekey: string } | null>(null);

  loading = signal<boolean>(false);
  error   = signal<string | null>(null);

  /** sponsorId from /r/:sponsorId — null or "0" means no sponsor. Persisted so it
   *  survives the five screens and a reload. */
  sponsorId = signal<string | null>(null);

  /**
   * Website V2 hand-over: the entry chosen on the public site (plan/weeks) and the
   * door the visitor came through (journey/goal). Sent with the lead; `plan` also
   * means the marketing screens were already read on the site, so the page opens
   * straight on the signup form.
   */
  intent: FunnelIntent = {};
  /** True when the visitor arrived from the website with a chosen entry. */
  fromWebsite = signal(false);

  firstNameCtrl = computed(() => this.form.get('first_name')!);
  emailCtrl     = computed(() => this.form.get('email')!);

  formStatus!: ReturnType<typeof toSignal<string>>;
  canSubmit!: ReturnType<typeof computed<boolean>>;

  constructor() {
    this.iconSet.icons = { ...this.iconSet.icons, cilUser, cilEnvelopeClosed };

    this.form = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name:  [''],
      email:      ['', [Validators.required, Validators.email]]
    });

    this.formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    this.canSubmit  = computed(() =>
      this.formStatus() === 'VALID' && !!this.captchaToken() && !this.loading()
    );

    // Sponsor, in the precedence of funnel-intent.ts SPONSOR_SOURCE_PRECEDENCE
    // (customer rule 2026-10-02: last touch until the first confirmed full payment).
    const query = this.route.snapshot.queryParamMap;
    let fromSession: string | null = null;
    try { fromSession = validSponsorId(sessionStorage.getItem(SESSION_SPONSOR_KEY)); } catch { /* ignore */ }
    const explicit = validSponsorId(this.route.snapshot.params['sponsorId']) ?? validSponsorId(query.get('ref'));
    const sponsor = resolveSponsor({
      path:    validSponsorId(this.route.snapshot.params['sponsorId']),
      query:   validSponsorId(query.get('ref')),
      session: fromSession,
      site:    readSiteReferral()
    });
    this.sponsorId.set(sponsor);
    if (explicit) {
      // A link in hand is the newest touch: it overwrites the tab's AND the
      // site's stored referral, so a later visit without a ref credits it.
      rememberReferral(explicit);
    } else if (sponsor) {
      try { sessionStorage.setItem(SESSION_SPONSOR_KEY, sponsor); } catch { /* ignore */ }
    }

    // plan/weeks/journey/goal from the website: a fresh query wins over what this
    // tab remembered (e.g. the visitor went back and picked another rhythm).
    const fromUrl = normaliseIntent({
      plan: query.get('plan'), weeks: query.get('weeks'),
      journey: query.get('journey'), goal: query.get('goal')
    });
    this.intent = Object.keys(fromUrl).length ? fromUrl : readStoredIntent();
    storeIntent(this.intent);
    if (fromUrl.plan) {
      // The website already did the persuading — skip the five screens.
      this.fromWebsite.set(true);
      this.screen.set((LAST_CONTENT_SCREEN + 1) as Screen);
      this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'website_handover', name: fromUrl.plan });
    }

    // The captcha widget is an iframe rendered into the signup screen's DOM, so
    // it can only be mounted once that screen exists — hence the screen() read.
    effect(() => { if (this.onSignup() && !this.mcaptchaConfig()) this.loadMcaptchaConfig(); });
    effect(() => { if (this.onSignup() && this.mcaptchaConfig()) this.loadMcaptchaWidget(); });

    window.addEventListener('message', this.handleMcaptchaMessage.bind(this));
  }

  // ---- Screen navigation -------------------------------------------------

  next(): void {
    const current = this.screen();
    if (current > LAST_CONTENT_SCREEN) return;

    const target = (current + 1) as Screen;
    this.screen.set(target);
    this.scrollToTop();
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'screen_view', name: `screen_${target}` });
  }

  back(): void {
    const current = this.screen();
    if (current <= 1) return;

    this.screen.set((current - 1) as Screen);
    this.scrollToTop();
  }

  /** Every screen has a CTA that jumps straight to signup — the funnel should
   *  never make a convinced visitor read the remaining screens first. */
  join(): void {
    if (this.onSignup()) return;

    this.screen.set((LAST_CONTENT_SCREEN + 1) as Screen);
    this.scrollToTop();
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'join_clicked', name: `screen_${this.screen()}` });
  }

  private scrollToTop(): void {
    // The funnel shell is its own scroll container (html/body are overflow:hidden
    // for the PWA shell), so window.scrollTo would be a no-op here.
    document.querySelector('.funnel-shell')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ---- mCaptcha ----------------------------------------------------------

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => this.mcaptchaConfig.set(cfg),
        error: err => {
          this.logger.error('failed to load mcaptcha config', err);
          this.error.set('Could not load captcha. Please refresh.');
        }
      });
  }

  private loadMcaptchaWidget(): void {
    const cfg = this.mcaptchaConfig();
    if (!cfg) return;

    const container = document.getElementById('mcaptcha-widget');
    if (!container) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${cfg.widgetUrl}?sitekey=${cfg.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = '';
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    // Origin comes from the configured widget URL (per-brand host), not a constant.
    const widgetUrl = this.mcaptchaConfig()?.widgetUrl;
    if (!widgetUrl) return;
    let expectedOrigin: string;
    try {
      expectedOrigin = new URL(widgetUrl).origin;
    } catch {
      return;
    }
    if (event.origin !== expectedOrigin) return;

    const data = event.data;
    let token: string | null = null;
    if (typeof data === 'string')   token = data;
    else if (data?.token)           token = data.token;
    else if (data?.m_captcha_token) token = data.m_captcha_token;

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
    }
  }

  // ---- Submit ------------------------------------------------------------

  onSubmit(): void {
    if (this.form.invalid || !this.captchaToken()) {
      if (!this.captchaToken()) this.captchaError.set(true);
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.funnel.createLead({
      first_name:    this.form.value.first_name?.trim(),
      last_name:     this.form.value.last_name?.trim() || undefined,
      email:         this.form.value.email,
      sponsor_id:    this.sponsorId(),
      language:      this.i18n.getLanguage() || 'en',
      captcha_token: this.captchaToken(),
      plan:          this.intent.plan ?? null,
      weeks:         this.intent.weeks ?? null,
      journey:       this.intent.journey ?? null,
      goal:          this.intent.goal ?? null
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.loading.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'lead_submitted',
            name:     res.existing_account ? 'existing_account' : 'new_lead'
          });
          const lang = this.i18n.getLanguage() || 'en';
          this.router.navigate(
            ['/', lang, 'quiz', 'check-email'],
            { state: { existingAccount: !!res.existing_account } }
          );
        },
        error: err => {
          this.loading.set(false);
          this.captchaToken.set(null); // single-use — reset so the user can retry
          this.error.set(err?.error?.error || 'Something went wrong. Please try again.');
        }
      });
  }
}
